import { TriangleAlert } from "lucide-react";
import { useState } from "react";
import { ErrorNotice } from "../../web/components/ui";
import type { DiffPart } from "../../web/lib/diff";
import { can, smartNameCheck, usePriceListSaver } from "./data";
import type { MaterialRow, PriceListData } from "./meta";
import { lineText, type NameTagConfig, splitName, tagText } from "./names";

/*
 * Names with their tags' wording (names.ts, smartTags.ts): the full name as it reads in ServiceM8, a name's change marked, and the
 * warning when an item's name doesn't have the wording its tags and their sets (its products' too) give it.
 */

/** Lines with where each starts in the text (a stable key for each). */
function withOffsets(lines: string[]) {
    const out: { line: string; offset: number }[] = [];
    let offset = 0;
    for (const line of lines) {
        out.push({ line, offset });
        offset += line.length + 1;
    }
    return out;
}

/**
 * The full name as it reads in ServiceM8, with the parts the tags add picked out (they're set with
 * the fields, not typed).
 */
export function ServiceM8Name({ name, tags, only }: Readonly<{ name: string; tags: NameTagConfig; only?: "first" | "rest" }>) {
    const parts = splitName(name, tags);
    const [first = "", ...rest] = parts.base.split(/\r?\n/);
    const brackets = tagText(parts, tags);
    const lines = tags.lines.filter((t) => parts.lines[t.id] !== undefined);
    // Just the first line (the name and its tags in brackets), or just the lines under it (e.g. for a one-line list).
    if (only === "first") {
        return (
            <>
                {first}
                {brackets ? <span className="font-medium text-brand-ink"> ({brackets})</span> : null}
            </>
        );
    }
    if (only === "rest") {
        const under = [...rest.map((line) => ({ text: line, tag: false })), ...lines.map((t) => ({ text: lineText(t, parts.lines[t.id] ?? ""), tag: true }))];
        return (
            <>
                {under.map((u, i) => (
                    <span key={`${i}:${u.text}`} className={u.tag ? "font-medium text-warning" : undefined}>
                        {i > 0 ? " · " : null}
                        {u.text}
                    </span>
                ))}
            </>
        );
    }
    return (
        <>
            {first}
            {brackets ? <span className="font-medium text-brand-ink"> ({brackets})</span> : null}
            {withOffsets(rest).map(({ line, offset }) => (
                <span key={offset}>
                    {"\n"}
                    {line}
                </span>
            ))}
            {lines.map((t) => (
                <span key={t.id} className="font-medium text-warning">
                    {"\n"}
                    {lineText(t, parts.lines[t.id] ?? "")}
                </span>
            ))}
        </>
    );
}

/** The full name as ServiceM8 shows it, below the tags that make it up (nothing when it has no tags: it's the same as typed). */
export function NamePreview({ name, tags, label = "Full name in ServiceM8" }: Readonly<{ name: string; tags: NameTagConfig; label?: string }>) {
    const parts = splitName(name, tags);
    if (!tagText(parts, tags) && !Object.keys(parts.lines).length) return null;
    return (
        <div className="space-y-1">
            <div className="field-label">{label}</div>
            <div className="name-preview rounded-(--field-radius) bg-surface-2 px-(--field-px) py-(--field-py) whitespace-pre-line">
                <ServiceM8Name name={name} tags={tags} />
            </div>
        </div>
    );
}

const DIFF_STYLE: Record<DiffPart["change"], string | undefined> = {
    same: undefined,
    removed: "rounded-sm bg-danger-soft text-danger line-through box-decoration-clone",
    added: "rounded-sm bg-brand-500/15 text-ink box-decoration-clone",
};

/** One line of a name's change, its changes marked (removed struck through, added highlighted). */
export function DiffLine({ parts }: Readonly<{ parts: readonly DiffPart[] }>) {
    return (
        <>
            {parts.map((part, i) => (
                <span key={`${i}:${part.change}`} className={DIFF_STYLE[part.change]}>
                    {part.text}
                </span>
            ))}
        </>
    );
}

/** It has more than one tag of a pick-one set (`text`: "More than one Installation tag"): which to keep is up to the person. Under its tags. */
export function TagClashNotice({ text }: Readonly<{ text: string }>) {
    return (
        <div className="flex items-center gap-2 border-t border-warning/30 bg-warning-soft px-3 py-2.5 text-warning-ink">
            <TriangleAlert className="size-4 shrink-0" />
            <p className="m-0">
                <strong>{text}:</strong> it allows only one. Take the others off (outlined above).
            </p>
        </div>
    );
}

/**
 * Its name doesn't read as its tags (its products' too) say: a tag changed, or its name was changed in ServiceM8. Like a
 * price to update: the name it would have, as the full name shows it, with Update name (as the person clicking). To keep the name
 * as it is, change its tags instead. Under its tags.
 */
export function SmartNameNotice({ data, item, bundle }: Readonly<{ data: PriceListData; item: Pick<MaterialRow, "uuid" | "name">; /** A bundle's name. */ bundle?: boolean }>) {
    const saver = usePriceListSaver();
    const [error, setError] = useState<Error | null>(null);
    const [busy, setBusy] = useState(false);
    const check = smartNameCheck(data, item);
    if (!check) return null;
    const update = () => {
        setBusy(true);
        setError(null);
        (bundle ? saver.updateBundleNameToTags(item.uuid) : saver.updateNameToTags(item.uuid))
            .catch((err: Error) => setError(err))
            .finally(() => setBusy(false));
    };
    return (
        <div className="space-y-2 border-t border-warning/30 bg-warning-soft px-3 py-2.5 text-warning-ink">
            <p className="m-0 flex items-center gap-2">
                <TriangleAlert className="size-4 shrink-0" />
                <strong>Name to update:</strong>
            </p>
            <div className="rounded-(--field-radius) bg-surface px-(--field-px) py-(--field-py) whitespace-pre-line text-ink">
                <ServiceM8Name name={check.name} tags={data.nameTags} />
            </div>
            {/* Why on the left, its button on the right (under it only when narrow), as in its price's warning. */}
            <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 sm:flex-nowrap">
                <p className="m-0 min-w-0 flex-1">Its name doesn&apos;t match its tags. To keep the name as it is, change its tags instead.</p>
                {can(data, bundle ? "bundles" : "items.edit") ? (
                    <button type="button" className="btn-primary shrink-0" disabled={busy} onClick={update}>
                        Update name
                    </button>
                ) : null}
            </div>
            <ErrorNotice error={error} />
        </div>
    );
}
